import React, { useEffect, useState } from 'react';
import {
  Stack,
  TableContainer,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Typography,
  Box,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClone, faTrashCan, faPencil, faCircleCheck, faBan } from '@fortawesome/pro-regular-svg-icons';
import capitalize from 'lodash/capitalize';
import ActionMenuButton from '../shared/action_menu_button';

// The sortable text form of a product's access: 'All schools', the joined school list, or '' for
// no access (sorts first).
const accessSortValue = (allowAll, schools) => {
  if (allowAll) return 'All schools';
  return (schools || []).join(', ');
};

const sortSurveyViewers = (surveyViewers, property, order) => {
  if (!surveyViewers) return [];
  const surveyViewersCopy = [...surveyViewers];
  let compareFunction;

  switch (property) {
    case 'name':
      compareFunction = (a, b) => {
        const valA = a.user?.full_name || '';
        const valB = b.user?.full_name || '';
        return valA.localeCompare(valB);
      };
      break;
    case 'email':
      compareFunction = (a, b) => {
        const valA = a.user?.email || '';
        const valB = b.user?.email || '';
        return valA.localeCompare(valB);
      };
      break;
    case 'spark_access':
      compareFunction = (a, b) => {
        const valA = accessSortValue(a.allow_access_to_all_schools, a.spark_schools);
        const valB = accessSortValue(b.allow_access_to_all_schools, b.spark_schools);
        return valA.localeCompare(valB);
      };
      break;
    case 'pulse_access':
      compareFunction = (a, b) => {
        const valA = a.has_pulse_survey_access ? accessSortValue(a.allow_access_to_all_schools_pulse, a.pulse_schools) : '';
        const valB = b.has_pulse_survey_access ? accessSortValue(b.allow_access_to_all_schools_pulse, b.pulse_schools) : '';
        return valA.localeCompare(valB);
      };
      break;
    case 'created_at':
      compareFunction = (a, b) => {
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return dateA - dateB;
      };
      break;
    default:
      compareFunction = () => 0;
  }

  return surveyViewersCopy.sort((a, b) => {
    let comparison = compareFunction(a, b);
    return order === 'asc' ? comparison : -comparison;
  });
};

const SortableTableHead = (props) => {
  const { order, orderBy, onRequestSort, showOrgColumn } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const headCells = [
    {
      id: 'name',
      numeric: false,
      label: 'Name',
      sortable: true,
      sx: { width: '200px' },
    },
    {
      id: 'email',
      numeric: false,
      label: 'Email',
      sortable: true,
      sx: { width: '200px' },
    },
    // Spark/Pulse Access carry no fixed width on purpose: they flex to absorb the table's spare
    // width, which pushes the snug Actions column to the right edge.
    {
      id: 'spark_access',
      numeric: false,
      label: 'Spark Access',
      sortable: true,
    },
    {
      id: 'pulse_access',
      numeric: false,
      label: 'Pulse Access',
      sortable: true,
    },
    // width 1% + nowrap shrinks the column to its content, so the left-aligned header label and
    // the Edit button share the same left edge and the buttons end flush with the table edge.
    {
      id: 'actions',
      numeric: false,
      label: 'Actions',
      sortable: false,
      sx: { width: '1%', whiteSpace: 'nowrap' },
    },
  ];

  return (
    <TableHead>
      <TableRow>
        {headCells
          .filter((cell) => {
            if (!showOrgColumn) {
              return cell.id !== 'org_name';
            } else {
              return true;
            }
          })
          .map((headCell) => (
            <TableCell
              key={headCell.id}
              align={headCell.align || 'left'}
              padding={'normal'}
              sortDirection={orderBy === headCell.id ? order : false}
              {...(headCell.sx && { sx: headCell.sx })}
            >
              {headCell.sortable ? (
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label}
                </TableSortLabel>
              ) : (
                headCell.label
              )}
            </TableCell>
          ))}
      </TableRow>
    </TableHead>
  );
};

// One product's access cell, distinguished by an icon per state so they scan at a glance:
// green check = all schools, gray chips = the named schools, gray ban + faint text = no access.
const noAccessCell = (
  <TableCell align="left">
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box component={FontAwesomeIcon} icon={faBan} sx={{ color: 'text.disabled' }} />
      <Typography variant="body2" color="text.disabled">
        No access
      </Typography>
    </Stack>
  </TableCell>
);

const AccessCell = ({ enabled = true, allowAll, schools }) => {
  if (!enabled) return noAccessCell;
  if (allowAll) {
    return (
      <TableCell align="left">
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box component={FontAwesomeIcon} icon={faCircleCheck} sx={{ color: 'success.main' }} />
          <Typography variant="body2">All schools</Typography>
        </Stack>
      </TableCell>
    );
  }
  if (!schools?.length) return noAccessCell;

  return (
    <TableCell align="left">
      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
        {schools.map((school) => (
          <Chip key={school} label={school} size="small" />
        ))}
      </Stack>
    </TableCell>
  );
};

const DataRow = ({ surveyViewer, onEdit, onDelete, onClone }) => {
  // Edit is the primary action and lives on the row itself; the … menu keeps the rest.
  const getMenuItems = (surveyViewerId) => {
    return [
      {
        label: 'Delete',
        icon: faTrashCan,
        onClick: () => onDelete(surveyViewerId),
      },
    ];
  };
  return (
    <TableRow data-cy={`survey-${surveyViewer.id}`}>
      <TableCell align="left">{surveyViewer.user.full_name}</TableCell>
      <TableCell align="left">{surveyViewer.user.email}</TableCell>
      <AccessCell allowAll={surveyViewer.allow_access_to_all_schools} schools={surveyViewer.spark_schools} />
      <AccessCell
        enabled={surveyViewer.has_pulse_survey_access}
        allowAll={surveyViewer.allow_access_to_all_schools_pulse}
        schools={surveyViewer.pulse_schools}
      />
      {/* The column is snug (width 1% + nowrap in the head), so plain left alignment already
          lines the buttons up under the header label, flush with the table edge. */}
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="contained" color="secondary" onClick={() => onEdit(surveyViewer)} data-cy={`button-edit-survey-viewer-${surveyViewer.id}`}>
            Edit
          </Button>
          <ActionMenuButton menuItems={getMenuItems(surveyViewer.id)} />
        </Stack>
      </TableCell>
    </TableRow>
  );
};

const DataTable = ({ surveyViewers, onEdit, onDelete, onClone, emptyMessage, showOrgColumn = false }) => {
  const [orderBy, setOrderBy] = useState(showOrgColumn ? 'org_name' : 'created_at');
  const [order, setOrder] = useState(showOrgColumn ? 'asc' : 'desc');
  const [sortedSurveyViewers, setSortedSurveyViewers] = useState(surveyViewers || []);

  useEffect(() => {
    setSortedSurveyViewers(sortSurveyViewers(surveyViewers, orderBy, order));
  }, [surveyViewers, orderBy, order]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);

    setSortedSurveyViewers(sortSurveyViewers(surveyViewers, property, newOrder));
  };

  if (surveyViewers.length > 0) {
    return (
      <TableContainer>
        <Table size={'small'} sx={{ marginTop: 4 }}>
          <SortableTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} showOrgColumn={showOrgColumn} />
          <TableBody>
            {sortedSurveyViewers.map((surveyViewer, index) => (
              <DataRow
                showOrgColumn={showOrgColumn}
                key={`${surveyViewer.id}_${index}`}
                surveyViewer={surveyViewer}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  } else {
    return <div className="text-center">{emptyMessage}</div>;
  }
};

export default DataTable;
